import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTable, StatisticalAnalysisIncidentsStatisticsDossierDetailRequest, StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart, StatisticalAnalysisIncidentsStatisticsDossierDetailViewChartResponseInternalGroups, StatisticalAnalysisIncidentsStatisticsDossierDetailViewTable, StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics, StatisticalAnalysisIncidentsStatisticsDossierGroupRequest, StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.dto'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { StatisticalAnalysisViewDetailChartData } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import { formatDatetimes, StatiscalAnalysisTypeCases, StatisticalAnalysisDynamicObject, StatisticalAnalysisIncidentsTimesTypeSelectorEnum, StatisticalAnalysisIncidentTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisIncidentsDossierExternalInfo } from '@/modules/statistical-analysis-module/views/incidents-dossier/incidents-dossier.model'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { SeriesOptionsType } from 'highcharts'
import { chain, find, isNil, map } from 'lodash'
import { store } from '@/store/store'

class IncidentsStatisticsDossierService {
    async getGeneralView(filters: StatisticalAnalysisIncidentsDossierExternalInfo): Promise<StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState[]> {
        const baseRequest = this.getBaseRequest(filters)
        const params = {
            entityTypes: (filters.typeSearch as StatisticalAnalysisSidebarItemCombo[]).map((entityType: StatisticalAnalysisSidebarItemCombo) => entityType.id),
            ...baseRequest
        }
        const response = await axios.get<StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState[]>(`/statisticAnalysis/dossiers/incidents`, { params })
        return response.data
    }

    async getGeneralViewStatistics(filters: StatisticalAnalysisIncidentsDossierExternalInfo): Promise<StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics> {
        const baseRequest = this.getBaseRequest(filters)
        const params = {
            entityTypes: (filters?.typeSearch as StatisticalAnalysisSidebarItemCombo[]).map((entityType: StatisticalAnalysisSidebarItemCombo) => entityType.id),
            infoDetail: filters.temporalFilter.id,
            infoType: filters.typeTimeFilter.id,
            ...baseRequest
        }
        const response = await axios.get<StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics>(`/statisticAnalysis/dossiers/incidents/${filters.trackingGeneralSelected?.key}`, { params })
        return response.data
    }

    async getDetailViewTable(
        filterModel: CommonFilterModel, params: CommonParams,
        entityTypes: StatiscalAnalysisTypeCases[],
        state: StatisticalAnalysisIncidentTypes, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest,
        detailRequest: StatisticalAnalysisIncidentsStatisticsDossierDetailRequest, scrollId: Nullable<string>): Promise<StatisticalAnalysisIncidentsStatisticsDossierDetailViewTable> {
        let extraParams = {
            ...filterModel,
            ...params,
            ...{ entityTypes: entityTypes },
            ...baseRequest,
            ...detailRequest
        }
        if (scrollId) {
            extraParams = {
                ...extraParams,
                ...{ scrollId }
            }
        }
        const response = await axios.get<StatisticalAnalysisIncidentsStatisticsDossierDetailViewTable>(`/statisticAnalysis/dossiers/incidents/${state}/detail`, { params: extraParams })
        return response.data
    }

    async getExternalViewGeneralList(filterModel: CommonFilterModel, commonParams: CommonParams, entityTypes: StatiscalAnalysisTypeCases[], state: StatisticalAnalysisIncidentTypes, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTable> {
        let params = {
            ...{ entityTypes },
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.get<StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/incidents/${state}/generalList`, { params })
        return response.data
    }

    async getExternalViewDetailList(filterModel: CommonFilterModel, commonParams: CommonParams, groupingParams: StatisticalAnalysisDynamicObject, typeCase: StatiscalAnalysisTypeCases[], baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, state: StatisticalAnalysisIncidentTypes, infoType: StatisticalAnalysisIncidentsTimesTypeSelectorEnum, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTable> {
        const bodyParams = {
            ...groupingParams
        }

        let params = {
            ...filterModel,
            ...commonParams,
            ...{ infoType },
            ...baseRequest,
            ...{ entityTypes: typeCase }
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.put<StatisticalAnalysisIncidentsStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/incidents/${state}/detailList`, bodyParams, { params })
        return response.data
    }

    async getDetailViewChart(entityTypes: StatiscalAnalysisTypeCases[], state: StatisticalAnalysisIncidentTypes, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, groupRequest: StatisticalAnalysisIncidentsStatisticsDossierGroupRequest): Promise<StatisticalAnalysisViewDetailChartData> {
        const params = {
            ...{ entityTypes },
            ...baseRequest,
            ...groupRequest
        }
        const response = await axios.get<StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/incidents/${state}/stats`, { params })
        return this._parseChartData(response.data)
    }

    async getExternalViewDetailChartList(caseIdRequest: number[]): Promise<StatisticalAnalysisViewDetailChartData> {
        const response = await axios.put<StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/incidents/detailStats`, { caseIdRequest })
        return this._parseChartData(response.data)
    }

    public getBaseRequest(filter: StatisticalAnalysisIncidentsDossierExternalInfo): StatisticalAnalysisIncidentsStatisticsDossierBaseRequest {
        return {
            startDate: encodeURIComponent(formatDatetimes(filter.startDate as Date)),
            endDate: encodeURIComponent(formatDatetimes(filter.endDate as Date)),
            laboratoryIds: (filter.laboratories as LaboratoryStatisticalAnalysisSidebarDateInput[]).map((item: LaboratoryStatisticalAnalysisSidebarDateInput) => item.labId),
            laboratoryUserId: store.getters.labId
        }
    }

    public generalExportTo(externalFilter: StatisticalAnalysisIncidentsDossierExternalInfo, bodyParams: ReportGridRequest, entityTypes: StatiscalAnalysisTypeCases[], exportType: CommonExportType): Promise<AxiosResponse> {
        const params = {
            ...{ entityTypes },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }
        return axios.put(`statisticAnalysis/dossiers/incidents/${externalFilter.trackingGeneralSelected?.key}/generalList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExportTo(
        externalFilter: StatisticalAnalysisIncidentsDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupByFields: string [],
        infoDetail: string,
        infoType: string,
        groupValueFilterRequests: StatisticalAnalysisDynamicObject[],
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...{ groupByFields },
            ...{ infoDetail },
            ...{ infoType },
            ...{ entityTypes: externalFilter.typeSearch!.map((entity: StatisticalAnalysisSidebarItemCombo) => entity.id) },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequests: groupValueFilterRequests
        }

        return axios.put(`statisticAnalysis/dossiers/incidents/${externalFilter.trackingGeneralSelected?.key}/detail/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExternalExportTo(
        externalFilter: StatisticalAnalysisIncidentsDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        infoDetail: string,
        infoType: string,
        groupValueFilterRequest: StatisticalAnalysisDynamicObject,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...{ infoDetail },
            ...{ infoType },
            ...{ entityTypes: externalFilter.typeSearch!.map((entity: StatisticalAnalysisSidebarItemCombo) => entity.id) },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequest
        }

        return axios.put(`statisticAnalysis/dossiers/incidents/${externalFilter.trackingGeneralSelected?.key}/detailList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    private _parseChartData(response: StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[]): StatisticalAnalysisViewDetailChartData {
        const categories = map(response, 'groupBy1')
        const labelSeries = chain(response).map('groupBy2').flatMap().map('name').uniq().value()
        const parsedResponse = this._getDataSeriesChart(labelSeries, response)
        const series = chain(parsedResponse).map('groupBy2').flatMap().groupBy('name').mapValues((value: any, key: string) => {
            return {
                name: key,
                data: map(value, 'count')
            }
        }).flatMap().value() as SeriesOptionsType[]
        return {
            categories,
            series
        }
    }

    private _getDataSeriesChart(uniqLabels: string[], response:StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[]): StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[] {
        return response.map((groupResponse: StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart) => {
            groupResponse.groupBy2 = uniqLabels.map((uniqLabel: string) => {
                const foundGroupBy2Item = find(groupResponse.groupBy2, { 'name': uniqLabel })
                if (foundGroupBy2Item) {
                    return foundGroupBy2Item
                } else {
                    return {
                        name: uniqLabel,
                        count: null
                    }
                }
            }) as StatisticalAnalysisIncidentsStatisticsDossierDetailViewChartResponseInternalGroups[]
            return groupResponse
        })
    }
}

export const incidentsStatisticsDossierService = new IncidentsStatisticsDossierService()
